// src/context/DashboardContext.js
import { createContext, useContext, useState, useEffect, useRef } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';

const DashboardContext = createContext();

export function DashboardProvider({ children }) {
    const [isLoading, setIsLoading] = useState(true);
    
    const [data, setData] = useState({});

    const [filters, setFilters] = useState('');
    const [currentChartFilter, setCurrentChartFilter] = useState('group-users');
    
    // Track ongoing requests to prevent concurrent calls
    const requestInProgressRef = useRef(false);

    const fetchDashboardData = async (filterParam = null, chartFilter = null) => {
        // Prevent concurrent requests
        if (requestInProgressRef.current) {
            return;
        }
        
        requestInProgressRef.current = true;
        setIsLoading(true);
        
        const activeFilters = filterParam !== null ? filterParam : filters;
        // Use provided chartFilter, or fall back to currentChartFilter, or default to 'group-users'
        const chartFilterToUse = chartFilter || currentChartFilter || 'group-users';
        
        // Build query string properly
        let queryString = activeFilters;
        if (queryString && !queryString.endsWith('&')) {
            queryString += '&';
        } else if (!queryString) {
            queryString = '';
        }
        queryString += `chart_filter=${chartFilterToUse}`;
        
        try {
            const response = await apiFetch({ path: `/wholesale/v1/wholesale-requests?${queryString}` });
            setData(response);
        } catch (error) {
            console.warn('Failed to fetch data: ', error);
        } finally {
            setIsLoading(false);
            requestInProgressRef.current = false;
        }
    };

    const fetchOrderChartData = async (filter, chartLoadingState, periodParams = null) => {
        // Store the current chart filter
        setCurrentChartFilter(filter);
        chartLoadingState(true);
        try {
            let queryString = `?filter=${filter}`;
            
            // Add period parameters if provided
            if (periodParams) {
                if (periodParams.period) {
                    queryString += `&period=${periodParams.period}`;
                }
                if (periodParams.start_date) {
                    queryString += `&start_date=${periodParams.start_date}`;
                }
                if (periodParams.end_date) {
                    queryString += `&end_date=${periodParams.end_date}`;
                }
            } else if (filters) {
                // Extract period from existing filters string
                const periodMatch = filters.match(/period=([^&]+)/);
                if (periodMatch) {
                    queryString += `&period=${periodMatch[1]}`;
                }
                
                // Extract custom date range if present
                const startDateMatch = filters.match(/start_date=([^&]+)/);
                const endDateMatch = filters.match(/end_date=([^&]+)/);
                if (startDateMatch) {
                    queryString += `&start_date=${startDateMatch[1]}`;
                }
                if (endDateMatch) {
                    queryString += `&end_date=${endDateMatch[1]}`;
                }
            }
            
            const response = await apiFetch({ path: `/wholesale/v1/wholesale-requests/orders${queryString}` });
            setData((prev) => {
                const updated = { ...prev, chart: response };
                return updated;
            });
        } catch (error) {
            console.warn("Failed to fetch card data:", error);
        } finally {
            chartLoadingState(false);
        }
    };

    const downloadOrderInvoice = async (orderId) => {
        if (!orderId) {
            return;
        }

        try {
            const invoiceResponse = await apiFetch({ 
                path: `/wholesale/v1/wholesale-requests/order-invoice?order_id=${orderId}` 
            });

            if (invoiceResponse?.url) {
                // Create a link and trigger download
                const link = document.createElement('a');
                link.href = invoiceResponse.url;
                link.download = invoiceResponse.fileName || `order-${orderId}-invoice.pdf`;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error) {
            console.error('Failed to download invoice', error);
            window.alert('Unable to download the invoice. Please try again.');
            throw error;
        }
    };

    useEffect(() => fetchDashboardData(), []);

    return (
        <DashboardContext.Provider value={{ data, isLoading, fetchDashboardData, setFilters, fetchOrderChartData, currentChartFilter, setCurrentChartFilter, downloadOrderInvoice }}>
            {children}
        </DashboardContext.Provider>
    );
}

export function useDashboard() {
    return useContext(DashboardContext);
}
